import React, { useContext, useEffect, useState } from 'react';
import {
  Result,
  ResultList as ResultListType,
  ResultTemplatesManager,
  SearchEngine,
  loadSearchActions,
  loadSearchAnalyticsActions
} from '@coveo/headless';
import { headlessEngine } from '../engine';
import ErrorMessage from '../../react-components/listing-states/error';
import Loader from '../../react-components/listing-states/loader';
import SecondCall from './second-call-context';
import { maxResultsPerPage } from '../controllers/controller';

type ResultListProps = {
  className?: string;
  additionalSettings?: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
  };
  resultTemplatesManager: ResultTemplatesManager;
  controller: ResultListType;
  preventFirstCall?: boolean;
  keyword?: string;
  resultsCustomizer?: (result: Result[]) => Result[];
};

function ResultList({
  className,
  additionalSettings,
  resultTemplatesManager,
  controller,
  preventFirstCall,
  keyword,
  resultsCustomizer
}: ResultListProps) {
  const classes = className || undefined;
  const { dataset } = document.getElementsByClassName(
    'coveo-listing__data-wrapper'
  )[0] as HTMLElement;
  const errorMessage = dataset.errorMessage as string;
  const noResultsMessage = dataset.noResultsMessage as string;
  const noResultsKeywordMessage = dataset.noResultsKeywordMessage as string;
  const noResultsAdditionalMessage =
    dataset.noResultsAdditionalMessage as string;
  const loaderText = dataset.loaderText as string;
  const [state, setState] = useState(controller.state);
  const [results, setResults] = useState<Result[]>([]);
  const secondCallContext = useContext(SecondCall);

  useEffect(() => {
    if (!preventFirstCall) {
      const { logInterfaceLoad } = loadSearchAnalyticsActions(
        headlessEngine as SearchEngine
      );
      const { executeSearch } = loadSearchActions(
        headlessEngine as SearchEngine
      );
      (headlessEngine as SearchEngine).dispatch(
        executeSearch(logInterfaceLoad())
      );
    }
    controller.subscribe(() => setState(controller.state));
  }, [controller]);

  useEffect(() => {
    if (resultsCustomizer) {
      setResults(resultsCustomizer(state.results));
    } else {
      setResults(state.results);
    }
  }, [state.results]);

  if (state.isLoading) {
    return <Loader loaderText={loaderText} />;
  }

  if (state.hasError) {
    <ErrorMessage
      errorMessage={errorMessage}
      noResultsMessage={noResultsMessage}
      noResultsKeywordMessage={noResultsKeywordMessage}
      showError={true}
    />;
  }

  if (!results.length) {
    return (
      <ErrorMessage
        errorMessage={errorMessage}
        noResultsMessage={noResultsMessage}
        noResultsKeywordMessage={noResultsKeywordMessage}
        noResultsAdditionalMessage={noResultsAdditionalMessage}
        keyword={keyword}
      />
    );
  }

  const renderResult = (result: Result, index: number) => {
    const customResult = {
      ...result,
      index: index + 1,
      additionalSettings
    };

    const template = resultTemplatesManager.selectTemplate(
      customResult
    ) as CallableFunction;

    if (!template)
      throw new Error(`No result template provided for ${customResult.title}.`);

    return template(customResult);
  };

  return (
    <ul className={classes}>
      {secondCallContext.secondCall
        ? secondCallContext.filteredResults.map((result, index) => {
            const page = secondCallContext.selectedPage;
            const start = (page - 1) * maxResultsPerPage;
            const end = page * maxResultsPerPage;

            if (index >= start && index < end)
              return renderResult(result, index);
            else return null;
          })
        : results?.map((result, index) => renderResult(result, index))}
    </ul>
  );
}

export default ResultList;
